// no-dd-sa:typescript-best-practices/boolean-prop-naming
/**
 * SEO Configuration
 *
 * Centralized metadata for all pages.
 * - Public pages: Indexable with SEO-optimized titles/descriptions
 * - Protected pages: noindex with analytics-friendly titles
 */

import { getToolBySlug } from '@/content/tools';
import { getFeaturePageBySlug } from '@/data/featurePages';

export interface PageMeta {
  title: string;
  description: string;
  noindex?: boolean;
}

const DEFAULT_META: PageMeta = {
  title: 'CodePulse',
  description: 'Engineering analytics for high-performing teams.',
  noindex: true,
};

export const SEO_CONFIG: Record<string, PageMeta> = {
  // ===== PUBLIC PAGES (indexable) =====
  '/': {
    title: 'CodePulse - Engineering Analytics for High-Performing Teams',
    description:
      'Track cycle times, DORA metrics, and team velocity from GitHub. See your engineering health score in 5 minutes.',
  },
  '/pricing': {
    title: 'Pricing | CodePulse',
    description:
      'Free forever for small teams. Pro starts at $12/dev/month. See exactly what you get at each tier—no hidden fees, no sales calls required.',
  },
  '/about': {
    title: 'About CodePulse',
    description: 'Who builds CodePulse and why: privacy-first, flat-priced engineering analytics for GitHub-native teams.',
  },
  // These four were routed and had inline <SEO> copy but no entry here, so
  // getPageMeta fell through to DEFAULT_META and SEO.tsx emitted
  // "noindex, nofollow" for all of them - /bus-factor is a free acquisition
  // tool that was telling Google not to index it. The title/description below
  // deliberately mirror each page's inline <SEO> props: the inline props win
  // at render time, so if these drift the config becomes a lie.
  '/bus-factor': {
    title: 'Bus Factor Analysis - Who Knows Your Codebase?',
    description:
      'Find every file with a bus factor of 1 before that knowledge walks out the door. Map code ownership and concentration risk across your repos in minutes.',
  },
  '/code-hotspots': {
    title: 'Code Hotspots - Find the Files That Keep Breaking',
    description:
      'A handful of files drive most of your incidents. CodePulse surfaces your highest-churn, highest-risk files in one view so you know exactly where to refactor first.',
  },
  '/github-analytics': {
    title: 'GitHub Analytics for Engineering Teams | CodePulse',
    description:
      'Native GitHub analytics without leaving your workflow. Get PR insights, cycle time breakdowns, review patterns, and team health metrics in 5 minutes.',
  },
  '/contact': {
    title: 'Contact Us',
    description:
      "Get in touch with the CodePulse team. We're here to help with questions about engineering analytics, team insights, and making your development workflow better.",
  },
  '/ai-code-review-impact': {
    title: 'Measure AI Code Review Impact | CodePulse',
    description: 'See what AI code review changed: cycle time, review turnaround, PRs per week - from your own GitHub data.',
  },
  '/5-minute-setup': {
    title: '5-Minute Setup | CodePulse',
    description: 'Connect your work GitHub org and see your engineering metrics in five minutes. No CI/CD hookup required.',
  },
  // Competitor comparison pages. These have their own entries rather than
  // leaning on the /alternative/ prefix branch in getPageMeta(), which returned
  // one generic title for all four - so anything reading meta from the registry
  // (rather than the page's inline <SEO>) described the wrong page. Keep in sync
  // with each page component's inline <SEO> title/description.
  '/alternative/linearb': {
    title: 'LinearB Alternative: Flat-Price GitHub Analytics',
    description:
      'LinearB costs $420-549 per contributor per year and shows symptoms, not causes. CodePulse is $199/mo flat, GitHub-native, live in 5 minutes. Free for 10 devs.',
  },
  '/alternative/swarmia': {
    title: 'Swarmia Alternative: Flat-Price GitHub Analytics',
    description:
      'Swarmia is EUR 42 per developer per month and shows the trend, not the cause. CodePulse is $199/mo flat, GitHub-native, live in 5 minutes. Free for 10 devs.',
  },
  '/alternative/jellyfish': {
    title: 'Jellyfish Alternative: $199 Flat vs Quote-Only',
    description:
      'Jellyfish is quote-only - buyers report a $35,920 median annual contract. CodePulse is $199/mo flat, GitHub-native, live in 5 minutes. Free for 10 devs.',
  },
  '/alternative/pluralsight-flow': {
    title: 'Pluralsight Flow Retiring: Where To Go Next',
    description:
      'Pluralsight Flow (Appfire) is EOL Dec 31, 2027 and renewals ended June 30, 2026. CodePulse is a flat-price, GitHub-native alternative, live in 5 minutes.',
  },
  '/how-it-works': {
    title: 'How It Works | CodePulse',
    description:
      'Connect your GitHub in 60 seconds. CodePulse automatically calculates DORA metrics, cycle times, and team velocity.',
  },
  '/security': {
    title: 'Security | CodePulse',
    description:
      'Enterprise-grade security for your engineering data. SOC 2-aligned controls, encrypted credentials, and read-only access.',
  },
  '/features': {
    title: 'Features - Engineering Analytics That Actually Help | CodePulse',
    description:
      'Cycle time breakdown, review network mapping, knowledge silo detection, board-ready metrics. See where engineering time actually goes.',
  },
  '/privacy': {
    title: 'Privacy Policy | CodePulse',
    description:
      'Your code stays yours - we never read your files. See exactly what CodePulse reads from GitHub, what it never touches, and how we protect it.',
  },
  '/terms': {
    title: 'Terms of Service | CodePulse',
    description:
      'Review CodePulse terms of service. Clear, readable policies covering your data rights, acceptable use, and service commitments.',
  },
  '/dpa': {
    title: 'Data Processing Agreement | CodePulse',
    description:
      'CodePulse Data Processing Agreement: how we process personal data as your UK GDPR processor, our sub-processors, security measures, and data-subject rights.',
  },
  '/stories': {
    title: 'Customer Stories | CodePulse',
    description:
      'Real engineering teams, real problems, real metrics. See how teams cut cycle time 63%, found $127K waste, and fixed knowledge silos with data.',
  },
  '/login': {
    title: 'Login | CodePulse',
    description:
      'Sign in to CodePulse to access your engineering analytics dashboard. Track DORA metrics, cycle times, and team velocity with GitHub-native insights.',
    // Deliberately indexable: "codepulse login" is a navigational query and
    // users expect to find the login page by searching for it. The reason to
    // noindex it was that /resources was prerendered into a canonical pointing
    // here - that is fixed by removing /resources from prerendering instead.
  },
  '/guides': {
    title: 'Engineering Intelligence Guides | CodePulse',
    description:
      'Practical guides for engineering leaders on metrics, team performance, code quality, and delivery optimization.',
  },
  '/tools': {
    title: 'Free Engineering Calculators (DORA, ROI, Cycle Time) | CodePulse',
    description:
      'Free engineering calculators and templates for DORA, cycle time, ROI, and team health. Get benchmarks in minutes.',
  },
  '/quiz': {
    title: 'Engineering Health Scorecard Quiz (2 Minutes) | CodePulse',
    description:
      '2-minute quiz reveals your team\'s hidden bottlenecks. Get a personalized engineering health score with specific fixes you can implement this week.',
  },
  '/playbook': {
    title: 'Engineering Metrics Playbook (Scripts + Templates) | CodePulse',
    description:
      'A practical playbook for rolling out engineering metrics without surveillance. Includes scripts, templates, and a 90-day plan.',
  },
  '/benchmark-report': {
    title: '2025 Engineering Benchmark Report (803K PRs) | CodePulse',
    description:
      'Engineering benchmarks from 802,979 merged pull requests. Compare cycle time, review practices, and delivery patterns.',
  },
  '/research': {
    title: 'Research | CodePulse',
    description:
      'Original engineering research with data-driven insights on pull requests, review patterns, and team health.',
  },

  // ===== LANDING PAGE VARIANTS (public, targeted) =====
  '/audit': {
    title: 'Engineering Health Audit | Identify Hidden Capacity',
    description:
      'Get a free engineering health audit in 15 minutes. Identify bottlenecks, knowledge silos, and risks in your GitHub repositories.',
  },
  '/developer-analytics': {
    title: 'Developer Analytics | Engineering Metrics for Teams',
    description:
      'Track developer productivity, DORA metrics, and team velocity. Real-time engineering analytics from your GitHub data.',
  },
  // Feature landing pages
  '/features/forecasting': {
    title: 'Delivery Forecasting for Eng Teams',
    description:
      'Stop guessing deadlines. Forecast delivery timelines from your actual GitHub velocity data. Monte Carlo simulations, confidence intervals, and sprint predictability.',
  },
  '/features/investment-allocation': {
    title: 'Engineering Investment Allocation',
    description:
      'Show the board where engineering time actually goes. Auto-classify work into features, bugs, tech debt, and infrastructure from GitHub data.',
  },
  '/features/space-framework': {
    title: 'SPACE Framework Dashboard for Engineering Teams',
    description:
      'Measure developer productivity across all 5 SPACE dimensions. Go beyond DORA with satisfaction, performance, activity, communication, and efficiency.',
  },
  '/features/risky-changes': {
    title: 'Risky PR Detection for Engineering Teams',
    description:
      'Detect 8 types of risky pull requests before they cause incidents. Rubber stamps, large PRs, no-approval merges, and more from your GitHub data.',
  },
  '/features/deployment-analytics': {
    title: 'DORA Deployment Metrics from Real Data',
    description:
      'Track deployment frequency, lead time, change failure rate, and MTTR from actual GitHub deployments. Not PR-merge proxies.',
  },
  '/features/alerts': {
    title: 'Engineering Alerts for Dev Teams',
    description:
      'Get notified before bottlenecks become blockers. Threshold-based alerts for cycle time, review coverage, and deployment health via Slack and email.',
  },
  '/features/sprint-retro': {
    title: 'Sprint Retro Reports From GitHub Data',
    description:
      'Replace subjective retrospectives with data-driven sprint reports. Throughput, cycle time breakdown, review patterns, and blockers from your actual GitHub data.',
  },
  '/features/review-insights': {
    title: 'Code Review Insights - Review Culture Analytics',
    description:
      'Measure review quality with sentiment analysis, rubber-stamp detection, and workload distribution. Build a healthier review culture from GitHub data.',
  },
  '/features/ai-tools-roi': {
    title: 'AI Coding Tools ROI Measurement',
    description:
      'Measure whether Copilot and AI coding tools are actually making your team faster. Before-and-after comparison from real GitHub data.',
  },
  '/engineering-code-intelligence': {
    title: 'Code Intelligence for Engineering Leaders | CodePulse',
    description:
      'Turn GitHub data into actionable engineering insights. Track cycle times, identify hotspots, and defend budgets with real metrics.',
  },
  '/sample-report': {
    title: 'Sample Engineering Report | See What CodePulse Delivers',
    description:
      'See the executive briefing CodePulse generates from your GitHub data. DORA metrics, risk insights, and actionable recommendations.',
  },
  // ===== PROTECTED PAGES (noindex) =====

  // Auth-gated, so it must report noindex here to match the X-Robots-Tag nginx
  // already sends via $cp_auth_noindex. It previously claimed to be indexable,
  // which is how it got prerendered into an indexable duplicate of /login.
  '/resources': {
    title: "Manager's Toolkit | Playbooks, Scripts & Templates",
    description:
      'Rollout scripts, velocity playbooks, and board presentation templates for engineering leaders implementing metrics programs.',
    noindex: true,
  },

  // Onboarding
  '/onboarding': {
    title: 'Setup | CodePulse',
    description: 'Complete your CodePulse setup.',
    noindex: true,
  },
  '/onboarding/prerequisites': {
    title: 'Prerequisites | CodePulse',
    description: 'Check prerequisites before setting up CodePulse.',
    noindex: true,
  },
  '/onboarding/select': {
    title: 'Select Organization | CodePulse',
    description: 'Select your GitHub organization to connect.',
    noindex: true,
  },

  // Core App
  '/dashboard': {
    title: 'Dashboard | CodePulse',
    description: 'Overview of your engineering metrics and team performance.',
    noindex: true,
  },
  '/repositories': {
    title: 'Repositories | CodePulse',
    description: 'View metrics for all connected repositories.',
    noindex: true,
  },
  '/repositories/compare': {
    title: 'Compare Repositories | CodePulse',
    description: 'Side-by-side repository comparison.',
    noindex: true,
  },
  '/developers': {
    title: 'Developer Profiles | CodePulse',
    description: 'Review individual developer metrics, activity, and contribution context.',
    noindex: true,
  },
  '/developer-league': {
    title: 'Developer League | CodePulse',
    description: 'A balanced view of contribution across delivery, reviews, and collaboration.',
    noindex: true,
  },

  // Awards & Leaderboards
  '/awards': {
    title: 'Awards | CodePulse',
    description: 'Developer achievements and recognition.',
    noindex: true,
  },
  '/test-champions': {
    title: 'Test Champions | CodePulse',
    description: 'Leaderboard for testing contributions.',
    noindex: true,
  },
  '/polyglot-leaderboard': {
    title: 'Polyglot Leaderboard | CodePulse',
    description: 'Developers working across multiple languages.',
    noindex: true,
  },

  // Insights & Analytics
  '/performance-insights': {
    title: 'Performance Insights | CodePulse',
    description: 'AI-powered performance analysis and recommendations.',
    noindex: true,
  },
  '/review-network': {
    title: 'Review Network | CodePulse',
    description: 'Visualize code review relationships.',
    noindex: true,
  },
  '/file-hotspots': {
    title: 'File Hotspots | CodePulse',
    description: 'Most frequently changed files.',
    noindex: true,
  },
  '/knowledge-silos': {
    title: 'Knowledge Silos | CodePulse',
    description: 'Identify single points of failure in code ownership.',
    noindex: true,
  },
  '/risky-changes': {
    title: 'Risky Changes | CodePulse',
    description: 'High-risk pull requests and commits.',
    noindex: true,
  },
  '/review-insights': {
    title: 'Code Review Insights | CodePulse',
    description: 'Code review patterns and metrics.',
    noindex: true,
  },
  '/executive': {
    title: 'Executive Summary | CodePulse',
    description: 'High-level metrics for leadership.',
    noindex: true,
  },

  // Settings & Management
  '/alerts': {
    title: 'Alerts | CodePulse',
    description: 'Configure metric alerts and thresholds.',
    noindex: true,
  },
  '/notification-history': {
    title: 'Notification History | CodePulse',
    description: 'History of sent notifications.',
    noindex: true,
  },
  '/team': {
    title: 'Access | CodePulse',
    description: 'Manage who can sign in to CodePulse and their roles.',
    noindex: true,
  },
  '/organizations': {
    title: 'Organizations | CodePulse',
    description: 'Manage connected organizations.',
    noindex: true,
  },
  '/billing': {
    title: 'Billing Settings | CodePulse',
    description: 'Manage subscription and billing.',
    noindex: true,
  },
  '/benchmarks': {
    title: 'Benchmarks | CodePulse',
    description: 'Compare against industry benchmarks.',
    noindex: true,
  },
  '/year-in-review': {
    title: 'Year in Review | CodePulse',
    description: 'Annual engineering metrics summary.',
    noindex: true,
  },
  '/velocity': {
    title: 'Velocity Graph | CodePulse',
    description:
      'Visualize your engineering team velocity with speed, throughput, quality, and collaboration metrics at a glance.',
    noindex: true,
  },
  '/space': {
    title: 'SPACE Dashboard | CodePulse',
    description:
      'SPACE framework metrics for measuring developer productivity and satisfaction.',
    noindex: true,
  },
  '/surveys': {
    title: 'Developer Surveys | CodePulse',
    description: 'Measure team satisfaction and wellbeing with developer surveys.',
    noindex: true,
  },
  '/pull-requests': {
    title: 'Pull Requests | CodePulse',
    description: 'View and analyze pull request activity across your repositories.',
    noindex: true,
  },

  // ===== ADMIN PAGES (noindex) =====
  '/admin': {
    title: 'Admin Dashboard | CodePulse',
    description: 'System administration dashboard.',
    noindex: true,
  },
  '/admin/growth': {
    title: 'Admin Growth | CodePulse',
    description: 'Growth metrics and analytics.',
    noindex: true,
  },
  '/admin/organizations': {
    title: 'Admin Organizations | CodePulse',
    description: 'Manage all organizations.',
    noindex: true,
  },
  '/admin/issues': {
    title: 'Admin Issues | CodePulse',
    description: 'System issues and health.',
    noindex: true,
  },
  '/admin/gift': {
    title: 'Admin Gift | CodePulse',
    description: 'Gift subscription management.',
    noindex: true,
  },
  '/admin/audit-log': {
    title: 'Admin Audit Log | CodePulse',
    description: 'System audit log.',
    noindex: true,
  },
  '/admin/cancellation-feedback': {
    title: 'Admin Cancellation Feedback | CodePulse',
    description: 'View cancellation feedback.',
    noindex: true,
  },
};

/**
 * Get page metadata for a given pathname.
 * Handles exact matches and dynamic routes.
 */
export function getPageMeta(pathname: string): PageMeta {
  // Exact match
  if (SEO_CONFIG[pathname]) {
    return SEO_CONFIG[pathname];
  }

  // Competitor comparison landing pages - conversion/SEO pages, always indexable.
  // Their full title/description is set inline via <SEO> in each page component;
  // this branch exists so the robots fallback never lands on DEFAULT_META's noindex.
  if (pathname.startsWith('/alternative/')) {
    return {
      title: 'CodePulse Comparison',
      description: 'Compare CodePulse with other engineering analytics platforms.',
    };
  }

  if (pathname.startsWith('/tools/')) {
    const segments = pathname.split('/').filter(Boolean);
    const slug = segments[1];
    const isEmbed = segments[2] === 'embed';

    if (isEmbed) {
      return {
        title: 'Embed Tool | CodePulse',
        description: 'Embedded CodePulse tool.',
        noindex: true,
      };
    }

    if (slug) {
      const tool = getToolBySlug(slug);
      if (tool) {
        return {
          title: `${tool.title} - Free Tool | CodePulse`,
          description: tool.metaDescription,
        };
      }
    }
  }

  // Feature detail pages
  if (pathname.startsWith('/features/')) {
    const slug = pathname.split('/').filter(Boolean)[1];
    if (slug) {
      const feature = getFeaturePageBySlug(slug);
      if (feature) {
        return {
          title: feature.metaTitle,
          description: feature.metaDescription,
        };
      }
    }
  }

  // Dynamic routes
  if (pathname.startsWith('/repositories/') && pathname !== '/repositories/compare') {
    return {
      title: 'Repository Details | CodePulse',
      description: 'Detailed metrics for this repository.',
      noindex: true,
    };
  }

  if (pathname.startsWith('/developers/')) {
    return {
      title: 'Developer Details | CodePulse',
      description: 'Detailed metrics for this developer.',
      noindex: true,
    };
  }

  if (pathname.startsWith('/stories/')) {
    // Story detail pages are public/indexable - title should be set dynamically
    return {
      title: 'Customer Story | CodePulse',
      description: 'Read how this team improved their engineering metrics with CodePulse.',
    };
  }

  if (pathname.startsWith('/guides/')) {
    // Guide detail pages are public/indexable - title should be set dynamically by GuideLayout
    return {
      title: 'Engineering Guide | CodePulse',
      description: 'In-depth guide for engineering leaders on improving team performance and metrics.',
    };
  }

  if (pathname.startsWith('/research/')) {
    // Research detail pages are public/indexable - title should be set dynamically by page SEO
    return {
      title: 'Research | CodePulse',
      description:
        'Original engineering research with data-driven insights on pull requests, review patterns, and team health.',
    };
  }

  if (pathname.startsWith('/share/')) {
    return {
      title: 'Shared View | CodePulse',
      description: 'Shared engineering metrics view.',
    };
  }

  if (pathname.startsWith('/team/')) {
    // Public team profiles are indexable - title should be set dynamically
    return {
      title: 'Team Profile | CodePulse',
      description: 'View this team\'s public engineering profile.',
    };
  }

  if (pathname.startsWith('/admin/organizations/')) {
    return {
      title: 'Admin Org Details | CodePulse',
      description: 'Organization administration.',
      noindex: true,
    };
  }

  if (pathname.startsWith('/auth/')) {
    return {
      title: 'Authentication | CodePulse',
      description: 'Authenticating with CodePulse.',
      noindex: true,
    };
  }

  // Default fallback
  return DEFAULT_META;
}
